import { Button } from '@offerzen/pinata'
import { useAnalytics } from 'clientHelpers/context/analyticsContext'
import { useFilters } from 'clientHelpers/context/filtersContext'
import BigModal from 'components/BigModal'
import GetStartedModal from 'components/GetStartedModal'
import SingleSelectFilterRolesFilter from 'components/SingleSelectFilterRolesFilter'
import SmallConversionModal from 'components/SmallConversionModal'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import FilterPopup from '../FilterPopup/FilterPopup'
import styles from './styles'

const MAX_SKILLS_SELECTED = 3
const LOCATION_ORDER = ['Other', 'Netherlands', 'South Africa', 'Germany', 'Ireland', 'Spain']

const FilterList = ({ hasFilterValues, applyFilters, loading }) => {
  const { setFilterValues, filters, ...filterValues } = useFilters()
  const [filterPopupsOpen, setFilterPopupsOpen] = useState({
    roles: false,
    locations: false,
    skills: false,
  })

  // one for each filter
  const onClickOutsideFilter = useCallback(() => {
    setFilterPopupsOpen({
      locations: false,
      roles: false,
      skills: false,
    })
  }, [filterPopupsOpen, setFilterPopupsOpen])

  const [skillsValidationError, setSkillsValidationError] = useState(false)

  const validateSkillsSelected = skillsSelected => {
    setSkillsValidationError(skillsSelected?.length > MAX_SKILLS_SELECTED)
  }

  const clearFilters = () => {
    const cleanFilterValues = { roles: null, locations: null, skills: null }
    applyFilters(cleanFilterValues)
    setFilterPopupsOpen({
      roles: false,
      locations: false,
      skills: false,
    })
  }

  const locations = filters?.locations?.sort((a, b) => LOCATION_ORDER.indexOf(a.country) - LOCATION_ORDER.indexOf(b.country)) || []

  useEffect(() => {
    validateSkillsSelected(filterValues?.skills)
  }, [filterValues])
  const { addTrackingForCTA } = useAnalytics()

  const formatFiltersIntoString = filterArray => {
    return filterArray?.map(filter => filter.name).join(', ') ?? ''
  }

  const [isModalOpen, setModalOpen] = useState(false)

  const onSearch = useCallback(() => {
    applyFilters(filterValues)
  }, [filterValues])

  return (
    <>
      <div className={styles.container}>
        <div className={styles.filtersWrapper}>
          {/* Roles Filter */}
          <SingleSelectFilterRolesFilter
            roles={filters?.roles || []}
            existingValue={formatFiltersIntoString(filterValues.roles)}></SingleSelectFilterRolesFilter>

          {/* Skills Filter */}
          <FilterPopup
            filterType="skills"
            open={filterPopupsOpen.skills}
            onClickOutsideFilter={onClickOutsideFilter}
            label="Must-have skills (max 3)"
            placeholder="Add skills"
            value={formatFiltersIntoString(filterValues.skills)}
            closeFilter={() =>
              setFilterPopupsOpen({
                locations: false,
                roles: false,
                skills: false,
              })
            }
            onToggle={() => {
              setFilterPopupsOpen({
                locations: false,
                roles: false,
                skills: !filterPopupsOpen.skills,
              })
            }}
            dropdownValues={filters?.skills || []}
            skillsValidationError={skillsValidationError}
            validateSkillsSelected={validateSkillsSelected}
          />
          {/* Location filter */}
          <FilterPopup
            filterType="location"
            open={filterPopupsOpen.locations}
            onClickOutsideFilter={onClickOutsideFilter}
            closeFilter={() =>
              // pass down state to close filters on mobile
              setFilterPopupsOpen({
                locations: false,
                roles: false,
                skills: false,
              })
            }
            label="Locations"
            placeholder="Hiring in"
            value={formatFiltersIntoString(filterValues.locations)}
            onToggle={() => {
              setFilterPopupsOpen({
                locations: !filterPopupsOpen.locations,
                skills: false,
              })
            }}
            onApply={locations => {
              setFilterValues({
                ...filterValues,
                locations,
              })
              setFilterPopupsOpen({
                roles: false,
                skills: false,
              })
            }}
            dropdownValues={locations}
          />
          <div className={styles.searchButtonWrapper}>
            <Button
              label="Search candidates"
              size="medium"
              theme="primary"
              variant="default"
              onClick={onSearch}
              className={styles.searchButton}
              disabled={
                skillsValidationError || loading || Object.values(filterPopupsOpen).some(filter => filter) // disable search if any filter modal is open
              }
            />
            {hasFilterValues() && (
              <a className={styles.clearSearchLinkDesktop} onClick={() => clearFilters()}>
                Clear search
              </a>
            )}
          </div>
          {hasFilterValues() && (
            <a className={styles.clearSearchLinkMobile} onClick={() => clearFilters()}>
              Clear search
            </a>
          )}
        </div>
      </div>
      <div className={styles.advancedFilters}>
        <SmallConversionModal
          sticky={true}
          placement="left"
          size="small"
          customClass={styles.modal}
          triggerText={
            <a
              {...addTrackingForCTA({
                action: 'Clicked Advanced Filters',
                sourceDetail: 'list filters',
              })}>
              Advanced filters <img src={`${process.env.NEXT_PUBLIC_BASEPATH}/assets/downArrow.svg`} alt="Arrow" />
            </a>
          }
          modalContent={
            <div className={styles.popUp}>
              <p>
                To enable advanced filters (e.g salary, visa status) and smarter matching,{' '}
                <a onClick={() => setModalOpen(true)} type="button">
                  sign up to OfferZen.
                </a>
              </p>
            </div>
          }
        />
      </div>
      <BigModal open={isModalOpen} handleClose={() => setModalOpen(false)}>
        {({ close }) => <GetStartedModal handleClose={close} />}
      </BigModal>
    </>
  )
}

FilterList.propTypes = {
  setFilterValues: PropTypes.func,
  filterValues: PropTypes.array,
  filters: PropTypes.object,
  hasFilterValues: PropTypes.func,
  applyFilters: PropTypes.func,
  loading: PropTypes.bool,
}

export default FilterList
